import { SideNavigation, SideNavigationProps } from '@cloudscape-design/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { subscribeToBatchesByStatus } from '../api/batches';
import { defaultMenuItems } from '../api/init';
import { useAuthContext } from '../contexts/AuthContext';
import { useModuleTranslation } from '../lang';

type MenuMap = Record<string, { title: string; href?: string; external?: boolean; disabled?: boolean }>;

const GROUPS: Array<{ id: string; ids: string[] }> = [
  { id: 'prod', ids: ['recipes', 'batches', 'planning', 'shopping-list', 'inventory', 'stock'] },
  { id: 'excise', ids: ['agp', 'audit'] },
  { id: 'sales', ids: ['sales', 'invoices'] },
  { id: 'tools', ids: ['tools', 'suggestions'] },
  { id: 'wiki', ids: [] },
];

const STORAGE_KEY = 'sidebar-expanded-groups';

const Sidebar = () => {
  const { currentUser, userObject, tenant, tier } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useModuleTranslation('Sidebar');

  const [activeHref, setActiveHref] = useState(location.pathname);

  // Optional: shopping badge state
  const [, setShopping] = useState(false);
  useEffect(() => {
    const unsub = subscribeToBatchesByStatus(['planned'], tenant, (batches) => setShopping(batches.length > 0));
    return unsub;
  }, [tenant]);

  // Allowed menu items
  const byId: MenuMap = useMemo(() => {
    const map: MenuMap = {};
    for (const it of defaultMenuItems.filter((x) => (x?.tier ?? 0) <= tier)) {
      map[it.id] = {
        title: t.menu[it.id as keyof typeof t.menu] || it.title,
        href: it.href,
        external: it.external,
        disabled: it.disabled,
      };
    }
    return map;
  }, [tier, t]);

  // Expanded state in localStorage (react-use handles persistence automatically)
  const [expandedGroups, setExpandedGroups] = useLocalStorage<Record<string, boolean>>(
    STORAGE_KEY,
    Object.fromEntries(GROUPS.map((g) => [g.id, true])),
  );
  const initialExpandedGroups = useRef(expandedGroups);

  // Build Cloudscape items
  const items: SideNavigationProps.Item[] = useMemo(() => {
    const makeLink = (id: string): SideNavigationProps.Link | null => {
      const mi = byId[id];
      if (!mi) return null;
      return {
        type: 'link',
        text: mi.title,
        href: mi.href ?? '',
        external: mi.external ?? mi.href?.startsWith('http'),
      };
    };

    const list: SideNavigationProps.Item[] = [{ type: 'link', text: t.home, href: '/' }];

    for (const g of GROUPS) {
      const children = g.ids.map(makeLink).filter(Boolean) as SideNavigationProps.Link[];

      if (children.length === 0) {
        // No children: use the group id as a direct link (e.g., 'wiki' -> defaultMenuItems['wiki'])
        const asLink = makeLink(g.id);
        if (asLink) list.push(asLink);
        continue;
      }

      if (children.length === 1) {
        // One child: flatten it for nicer UX
        list.push(children[0]);
        continue;
      }

      // Multiple children: keep as expandable group
      list.push({
        type: 'expandable-link-group',
        text: t.groups[g.id as keyof typeof t.groups],
        href: g.id,
        defaultExpanded: !!initialExpandedGroups.current?.[g.id],
        items: children,
      });
    }

    list.push({ type: 'link', text: t.settings, href: '/settings' });
    return list;
  }, [byId, t]);

  useEffect(() => setActiveHref(location.pathname), [location]);

  return (
    <SideNavigation
      header={{ href: '/', logo: { alt: t.logoAlt, src: '/logo_2.png' } }}
      activeHref={activeHref}
      items={!currentUser || !userObject?.approved ? [] : items}
      onFollow={(event) => {
        const { href, external, type } = event.detail;

        if (type === 'expandable-link-group') {
          event.preventDefault();
          return;
        }

        if (!external && href) {
          event.preventDefault();
          setActiveHref(href);
          navigate(href);
        }
      }}
      onChange={(event) => {
        const { item, expanded } = event.detail;
        if ('href' in item && GROUPS.some((g) => g.id === item.href)) {
          setExpandedGroups({
            ...(expandedGroups || {}),
            [item.href]: expanded,
          });
        }
      }}
    />
  );
};

export default Sidebar;
